import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErsIdentityService } from '../services/ers-identity/ers-identity.service';
import { ersConstants } from '@rcs/utils';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';
import { Store } from '@ngrx/store';
import { logOut } from '../base/+store/actions/ers-identity.actions';

@Injectable()
export class ErsAuthInterceptor implements HttpInterceptor {
  private service = inject(ErsIdentityService);
  private store = inject(Store);
  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  retriveItemFromLs = (key: string): string | null =>
    isPlatformBrowser(this.platformId) ? localStorage.getItem(key) : ``;

  private getTenantId(): string | null {
    const raw = this.retriveItemFromLs(ersConstants.SELECTED_TENANT);
    if (!raw) return null;
    try {
      const tenant = JSON.parse(raw);
      return tenant?.id ?? null;
    } catch {
      return null;
    }
  }

  private isGlRequest(url: string): boolean {
    return url.startsWith(environment.apiUrl.generalLedger);
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip adding token for public onboarding/registration endpoints
    const isPublicEndpoint = request.url.includes('/api/User/savecommonregistration') ||
      request.url.includes('/api/Authentication/passwordsendemail') ||
      request.url.includes('Authentication/passwordsendemail') ||
      request.url.includes('/api/MasterData/getallcountries') ||
      request.url.includes('/api/MasterData/getallstatesbycountryid') ||
      request.url.includes('/api/MasterData/getallcitiesbystateid');

    if (isPublicEndpoint) {
      return next.handle(request);
    }

    const glRequest = this.isGlRequest(request.url);
    const headers: Record<string, string> = {};

    const tenantId = this.getTenantId();
    if (tenantId && !glRequest) {
      headers['X-Tenant-Id'] = tenantId;
    }

    if (this.service.isTokenExpired()) {
      if (request.url.includes('bot_management')) {
        headers['jwt-authorization'] = `Bearer ${this.retriveItemFromLs(ersConstants.ERS_JWT_TOKEN)}`;
        headers['Authorization'] = this.retriveItemFromLs(ersConstants.BOT_TOKEN) ?? '';
      } else {
        headers['Authorization'] = request.url.includes('search_with_bot_token')
          ? `${this.retriveItemFromLs(ersConstants.BOT_TOKEN)}`
          : `Bearer ${this.retriveItemFromLs(ersConstants.ERS_JWT_TOKEN)}`;
        if (!glRequest && request.url.includes('search_with_bot_token')) {
          headers['jwt-authorization'] = `Bearer ${this.retriveItemFromLs(ersConstants.ERS_JWT_TOKEN)}`;
        }
      }
    }

    if (Object.keys(headers).length > 0) {
      request = request.clone({ setHeaders: headers });
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && err.error?.error === 'SESSION_INVALIDATED') {
          // Flag the login page to show a persistent notice. sessionStorage is
          // per-tab (only THIS evicted tab shows it) and survives the redirect to
          // /login. Read + cleared in LoginComponent.ngOnInit.
          if (isPlatformBrowser(this.platformId)) {
            sessionStorage.setItem('ers_session_ended', '1');
          }
          this.store.dispatch(logOut());
        }
        return throwError(() => err);
      })
    );
  }
}
